// Stops the 401 -> reload -> Keycloak -> 401 loop from running forever.
//
// The reload itself is deliberate and stays: an access token can expire while a
// long request is in flight (a ProseVector analysis runs for a minute or more),
// and reloading into a fresh login is the right recovery. That path was
// tightened on purpose and must not be removed.
//
// What it cannot do is tell "my token just expired" from "authentication is
// broken and will fail again immediately". In the second case it reloads
// forever, and the loop destroys its own evidence: the console error from
// keycloak-js is wiped by the next navigation before anyone can read it. The
// user sees a flicker, and a developer sees nothing at all.
//
// So: allow the reload the first couple of times, then stop and let the error
// reach the screen. Counting lives in sessionStorage because it must survive a
// reload (localStorage would also survive the tab, which would be wrong -- a
// new tab deserves a fresh chance).

const KEY = 'auth_reload_attempts'
const LOGIN_KEY = 'auth_login_attempts'

export const DEFAULTS = { windowMs: 30_000, max: 2 }

/**
 * Records this attempt and says whether reloading is still reasonable.
 *
 * Returns true for the first `max` attempts inside `windowMs`, false after.
 * Attempts older than the window are discarded, so an expiry today and another
 * next week are unrelated rather than cumulative.
 */
export function shouldReloadForAuth (storage, nowMs = Date.now(), opts = {}) {
  const { windowMs, max } = { ...DEFAULTS, ...opts }
  // No storage (SSR, a locked-down browser) means no way to count. Allow the
  // reload: the recovery path matters more than the loop guard.
  if (!storage) return true

  const recent = readAttempts(storage).filter((t) => nowMs - t < windowMs)
  recent.push(nowMs)
  write(storage, recent)

  return recent.length <= max
}

/**
 * Called once authentication succeeds, so a later genuine expiry gets a clean
 * budget instead of inheriting a spent one.
 */
export function clearAuthReloadAttempts (storage) {
  try {
    storage?.removeItem(KEY)
  } catch {
    // A storage that throws on write is not a reason to fail a login.
  }
}

function readAttempts (storage) {
  try {
    const raw = storage.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'number') : []
  } catch {
    return []
  }
}

function write (storage, attempts) {
  try {
    storage.setItem(KEY, JSON.stringify(attempts))
  } catch {
    // Ignore: a full or blocked storage should not break the reload path.
  }
}

// The reload budget above guards the 401 path in client.js. It is not enough on
// its own: with `onLoad: 'login-required'`, keycloak-js redirects to Keycloak
// ITSELF, before the app mounts and before any API call happens. So a broken
// handshake bounces the user straight back to the login screen and no code of
// ours ever runs to say why.
//
// This budget guards that redirect. Once spent, useAuth switches to
// 'check-sso', which does NOT redirect -- the app mounts unauthenticated and
// can finally show the reason instead of flickering.
export function shouldAttemptLogin (storage, nowMs = Date.now(), opts = {}) {
  const { windowMs, max } = { ...DEFAULTS, ...opts }
  if (!storage) return true

  const recent = readList(storage, LOGIN_KEY).filter((t) => nowMs - t < windowMs)
  recent.push(nowMs)
  writeList(storage, LOGIN_KEY, recent)

  return recent.length <= max
}

export function clearLoginAttempts (storage) {
  try {
    storage?.removeItem(LOGIN_KEY)
  } catch { /* storage unavailable */ }
}

function readList (storage, key) {
  try {
    const parsed = JSON.parse(storage.getItem(key) || "[]")
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === "number") : []
  } catch {
    return []
  }
}

function writeList (storage, key, list) {
  try {
    storage.setItem(key, JSON.stringify(list))
  } catch { /* storage unavailable */ }
}
